<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import {
  AccountRestrictionNotice,
  KycActionBlocker,
  KycReviewNotice,
  type KycActionState,
} from '@modules/account'
import { canTransactInWallet } from '../utils/walletAccessUtils'

interface Props {
  // At least one linked bank account (any verification status).
  hasBankAccount?: boolean
  // At least one *verified* ('active') bank account.
  hasActiveBank?: boolean
  // Account access restricted: replaces the action buttons with a
  // contact-support blocker. Takes precedence over `kycState`.
  restricted?: boolean
  // KYC gate. 'action-required' → "Complete KYC" blocker; 'in-review' →
  // Add Funds / Withdraw when a verified bank exists, else a
  // verification-in-progress notice; 'verified' → actions when a bank exists,
  // else "Add bank account".
  kycState?: KycActionState
}

const props = withDefaults(defineProps<Props>(), {
  hasBankAccount: false,
  hasActiveBank: false,
  restricted: false,
  kycState: 'verified',
})

const emit = defineEmits<{
  (e: 'add-funds'): void
  (e: 'withdraw'): void
  (e: 'add-bank'): void
}>()

// True for the states that reach the final action region with money-moving
// unlocked: verified + linked bank, or in-review + verified bank. Otherwise the
// final region shows the "Add bank account" CTA (verified, no bank).
const showActionButtons = computed(() =>
  canTransactInWallet({
    restricted: props.restricted,
    kycState: props.kycState,
    hasBankAccount: props.hasBankAccount,
    hasActiveBank: props.hasActiveBank,
  }),
)

const onAddFunds = () => emit('add-funds')
const onWithdraw = () => emit('withdraw')
</script>

<template>
  <Box align="center" class="qa-card">
    <Box class="qa-card__blur qa-card__blur--tr" />
    <Box class="qa-card__blur qa-card__blur--bl" />

    <Box direction="col" gap="3" class="qa-card__inner">
      <Box align="start" justify="between">
        <Box direction="col" gap="1">
          <Text type="title-lg" is="span" class="qa-card__title">Quick Actions</Text>
          <Text type="body" weight="medium" color="muted" is="span" class="qa-card__subtitle">
            Manage your funds easily
          </Text>
        </Box>
        <PiIcon name="building-columns" class="qa-card__icon" />
      </Box>

      <AccountRestrictionNotice
        v-if="restricted"
        description="Adding funds and withdrawals are unavailable while your account access is restricted. Please contact support for more information."
      />

      <KycActionBlocker
        v-else-if="kycState === 'action-required'"
        description="Adding funds and withdrawals are unavailable until your identity is verified. Complete your KYC to start transacting."
      />

      <KycReviewNotice v-else-if="kycState === 'in-review' && !hasActiveBank" />

      <Box v-else align="center" gap="3" class="qa-card__actions">
        <template v-if="showActionButtons">
          <AppButton label="Add Funds" block class="qa-btn qa-btn--primary" @click="onAddFunds" />
          <AppButton
            label="Withdraw"
            variant="outline"
            block
            class="qa-btn qa-btn--secondary"
            @click="onWithdraw"
          />
        </template>
        <AppButton
          v-else
          label="Add bank account"
          icon="plus"
          block
          class="qa-btn qa-btn--primary"
          @click="emit('add-bank')"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.qa-card {
  position: relative;
  flex: 1;
  min-width: 0;
  background: var(--p-surface-50);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 5px 1.25rem;
  box-shadow: 0 4px 12px rgba(10, 13, 18, 0.06);
  overflow: hidden;
  box-sizing: border-box;
}

.qa-card__blur {
  position: absolute;
  width: 96px;
  height: 96px;
  border-radius: 9999px;
  background: var(--p-primary-100);
  pointer-events: none;
}

.qa-card__blur--tr {
  filter: blur(12px);
  top: auto;
  bottom: 97px;
  right: 10px;
}

.qa-card__blur--bl {
  filter: blur(12px);
  bottom: -21px;
  left: -24px;
}

.qa-card__inner {
  width: 100%;
  position: relative;
  z-index: 1;
}

.qa-card__title {
  color: var(--p-text-muted-color);
  line-height: 30px;
  white-space: nowrap;
}

.qa-card__subtitle {
  line-height: 20px;
  white-space: nowrap;
}

.qa-card__icon {
  font-size: 40px;
  color: var(--p-primary-600);
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qa-card__actions {
  width: 100%;
}

/* True-secondary outline for Withdraw — keeps Add Funds as the only filled primary CTA. */
.qa-btn--secondary :deep(.base-button) {
  background: transparent !important;
  border: 1px solid var(--p-primary-200) !important;
  color: var(--p-primary-700) !important;
  box-shadow: none !important;
}

.qa-btn--secondary :deep(.base-button:not(:disabled):hover) {
  background: var(--p-primary-50) !important;
  border-color: var(--p-primary-300) !important;
  color: var(--p-primary-700) !important;
}
</style>
